import { Link } from "wouter";
import { PenLine } from "lucide-react";

export default function Terms() {
  return (
    <div className="min-h-[100dvh] flex flex-col bg-background">
      <header className="px-6 py-4 flex items-center justify-between border-b border-border bg-white">
        <Link href="/" className="flex items-center gap-2">
          <PenLine className="h-6 w-6 text-primary" />
          <span className="text-xl font-bold tracking-tight text-card-foreground">ClassTrack</span>
        </Link>
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-6 py-16">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Terms of Service</h1>
        <p className="text-sm text-gray-400 mb-10">Last updated: June 2026</p>

        <div className="prose prose-gray max-w-none space-y-8 text-gray-700 leading-relaxed">

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Acceptance of terms</h2>
            <p>
              By accessing or using ClassTrack ("the Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use the Service.
            </p>
            <p className="mt-3">
              These Terms form a legally binding agreement between you and ClassTrack, governed by the laws of New South Wales, Australia. By using the Service you confirm that you are at least 18 years of age and have the legal capacity to enter into this agreement.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">1a. About us</h2>
            <p>
              ClassTrack is operated by The trustee for The ASSIDA Unit Trust, ABN 26 760 767 802, trading as ClassTrack.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Description of service</h2>
            <p>
              ClassTrack is a web-based planning and documentation tool designed for teachers. It allows you to create lesson plans, diary entries, student records, behavioural notes, and related professional documentation. ClassTrack supports your professional record-keeping but <strong>does not replace your school's official student information system</strong> (such as Sentral, Compass or equivalent). If your school relies on those systems for compliance reporting, you remain responsible for transferring the relevant information into them; ClassTrack provides exports to assist with this where you choose to use them. The Service is provided on a subscription basis. New users may receive a free trial of full Pro features. Continuing access requires a paid individual or school subscription.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Account registration</h2>
            <p>
              You must create an account to use ClassTrack. You agree to:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Provide accurate, current, and complete information during registration</li>
              <li>Keep your account credentials confidential</li>
              <li>Notify us immediately if you believe your account has been compromised</li>
              <li>Be responsible for all activity that occurs under your account</li>
            </ul>
            <p className="mt-3">
              You may not create an account on behalf of another person without their authorisation.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">3a. Teacher responsibilities</h2>
            <p>As the teacher using ClassTrack, you are responsible for:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Checking your school's policy on third-party tools, cloud services and student data before entering student information.</li>
              <li>Using the least identifying student label that works — initials, preferred names or student codes where possible.</li>
              <li>Avoiding entering unnecessary sensitive information (medical detail, family circumstances, contact details) into free-text fields and AI-assisted fields.</li>
              <li>Transferring records into your school's official student information system (such as Sentral, Compass or equivalent) where your school requires it. ClassTrack provides exports to help with this; it does not replace those systems.</li>
            </ul>
            <p className="mt-3">
              See also the <Link href="/privacy#school-access" className="text-primary underline">"School accounts and head-teacher access" paragraph in our Privacy Policy</Link> for what information may be visible to your school's leadership users when you join a school account.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Free trial, subscriptions, and read-only mode</h2>
            <p>
              ClassTrack may offer a free trial. No credit card is required to start the trial.
            </p>
            <p className="mt-3">
              When your trial ends — or if your paid subscription lapses — your account switches to <strong>read-only mode</strong>. Your existing data is kept safe: you can still sign in, view everything you've created, and export your records and lesson plans. You will not be able to add, edit, or delete content until you subscribe.
            </p>
            <p className="mt-3">
              Pricing for the individual Pro plan is confirmed at the time of subscription. Whole-school rollouts are available on request and are subject to a separate school agreement.
            </p>
            <p className="mt-3">
              Subscriptions automatically renew at the end of each billing period. You authorise us to charge your payment method on each renewal date until you cancel.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Cancellation and refunds</h2>
            <p>
              You may cancel your subscription at any time through the billing portal in your account settings. Cancellation takes effect at the end of your current billing period — you will continue to have access to Pro features until then.
            </p>
            <p className="mt-3">
              We do not offer refunds for partial billing periods. If you believe you have been charged in error, please contact us within 14 days and we will investigate.
            </p>
            <p className="mt-3">
              Under Australian Consumer Law, you may be entitled to a refund if the Service fails to meet a consumer guarantee. Please contact us if you believe this applies.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Acceptable use</h2>
            <p>You agree to use ClassTrack only for lawful purposes and in accordance with these Terms. You must not:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Use the Service to store or transmit material that is unlawful, harmful, defamatory, or discriminatory</li>
              <li>Attempt to gain unauthorised access to any part of the Service or its related systems</li>
              <li>Use the Service to send spam or unsolicited communications</li>
              <li>Reverse engineer, decompile, or otherwise attempt to extract the source code of the Service</li>
              <li>Resell, sublicense, or otherwise commercialise access to the Service</li>
              <li>Use the Service in a way that could damage, overload, or impair its operation</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Your content</h2>
            <p>
              You retain ownership of all content you create using ClassTrack, including lesson plans, notes, and student records ("Your Content"). By using the Service, you grant us a limited, non-exclusive licence to store and process Your Content solely to provide and improve the Service.
            </p>
            <p className="mt-3">
              You are responsible for ensuring that Your Content — including any student information — is handled in accordance with applicable privacy laws and your school's data policies. You must not enter student information that you do not have the right to record. If you join a school account, please refer to the "School accounts and head-teacher access" paragraph in our <Link href="/privacy" className="text-primary underline">Privacy Policy</Link> for what may be visible to your school's leadership users.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Intellectual property</h2>
            <p>
              ClassTrack and all related technology, software, branding, and content (excluding Your Content) are owned by us and protected by Australian and international intellectual property laws. Nothing in these Terms transfers any intellectual property rights to you.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Availability and changes to the service</h2>
            <p>
              We aim to keep ClassTrack available at all times, but we do not guarantee uninterrupted access. The Service may be unavailable due to maintenance, technical issues, or circumstances beyond our control.
            </p>
            <p className="mt-3">
              We may modify or discontinue features of the Service at any time. Where we make significant changes that affect your use, we will endeavour to provide reasonable notice.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Limitation of liability</h2>
            <p>
              To the fullest extent permitted by Australian law, ClassTrack is not liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of or inability to use the Service, including loss of data or lost profits.
            </p>
            <p className="mt-3">
              Our total liability to you for any claim arising under these Terms is limited to the amount you paid to us in the 12 months preceding the claim.
            </p>
            <p className="mt-3">
              Nothing in these Terms excludes, restricts, or modifies any consumer right or guarantee that cannot be excluded under the <em>Australian Consumer Law</em>, including any applicable guarantee of acceptable quality or fitness for purpose.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">11. Termination</h2>
            <p>
              We may suspend or terminate your account if you breach these Terms or if your conduct poses a risk to other users or the integrity of the Service. We will notify you where practicable before doing so.
            </p>
            <p className="mt-3">
              You may delete your account at any time via your account settings. Upon deletion, your data will be removed in accordance with our <Link href="/privacy" className="text-primary underline">Privacy Policy</Link>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">12. Governing law and disputes</h2>
            <p>
              These Terms are governed by the laws of New South Wales, Australia. Any disputes arising from these Terms or your use of the Service will be subject to the exclusive jurisdiction of the courts of New South Wales, Australia.
            </p>
            <p className="mt-3">
              Before commencing legal proceedings, we ask that you contact us to resolve any dispute informally.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">13. Changes to these terms</h2>
            <p>
              We may update these Terms from time to time. When we do, we will update the "Last updated" date at the top of this page. If the changes are material, we will notify you by email or in-app notice. Continued use of ClassTrack after the updated Terms take effect constitutes your acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">14. Contact us</h2>
            <p>
              For any questions about these Terms, please contact us at:
            </p>
            <p className="mt-3 font-medium">The trustee for The ASSIDA Unit Trust (ABN 26 760 767 802), trading as ClassTrack<br />Australia<br />
              <a href="mailto:classtrack.com.au@gmail.com" className="text-primary underline">classtrack.com.au@gmail.com</a>
            </p>
          </section>

        </div>
      </main>

      <footer className="bg-gray-900 text-gray-400 py-8 px-6 text-center text-sm">
        <div className="flex justify-center gap-6 mb-3">
          <Link href="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
          <Link href="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
          <Link href="/legal/subprocessors" className="hover:text-white transition-colors">Subprocessors</Link>
        </div>
        <p>&copy; 2026 ClassTrack. All rights reserved.</p>
      </footer>
    </div>
  );
}
